import ConfirmationPanel from 'components/form/confirmation/confirmation-panel';
import { ApplicationManagerClient } from 'clients/application-manager-client';
import React from 'react';
import Validation from 'components/form/validation/validation';
import { Controller, useForm } from 'react-hook-form';
import { Grid } from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { nameOfFactory } from 'utils/types-util';
import { ReactSelect } from 'components/form/field/react-select';
import { NumberOption } from 'interfaces/forms/types';
import { LicensedStatesTypeFormStyles } from './licensed-states-form.styles';
import { IFormFields } from './types';
import { logger } from '../../../winston-logger';

type PermissionsTypeFormFields = IFormFields<NumberOption[]>;

interface IPermissionsFormProps extends WithStyles<typeof LicensedStatesTypeFormStyles> {
  initialValue: number[];
  handleCancel: () => void;
  handleSubmit: (value: number[]) => void;
}

const getFieldName = nameOfFactory<PermissionsTypeFormFields>();

const PermissionsForm: React.FC<IPermissionsFormProps> = (
  props: IPermissionsFormProps,
): JSX.Element => {
  const { classes } = props;

  // #region component state
  const {
    handleSubmit,
    control,
    setError,

    formState: { errors },
  } = useForm<any>();
  const [permissions, setPermissionOptions] = React.useState<any[]>([]);
  const [defaultValue, setDefaultValue] = React.useState<any[]>([]);

  React.useEffect(() => {
    (async () => {
      // Load permissions
      try {
        const permissions = await ApplicationManagerClient.getPermissionsAssignablePerCustomer();
        setPermissionOptions(
          permissions.data.map(x => {
            return { label: x.display_name, value: x.id };
          }),
        );
      } catch (error) {
        logger.error(error);
      }
    })();
  }, []);

  React.useEffect(() => {
    setDefaultValue(permissions.filter(x => props.initialValue.includes(x.value)));
  }, [permissions, props.initialValue]);
  // #endregion

  // #region helpers
  const onSubmit = (formValues: PermissionsTypeFormFields) => {
    if (formValues.selectedValue.length === 0) {
      setError(getFieldName('selectedValue'), { type: 'manual' });
    } else {
      const formattedValue = formValues.selectedValue.map(x => x.value);

      props.handleSubmit(formattedValue);
    }
  };

  const onCancel = () => {
    props.handleCancel();
  };

  // #endregion

  return (
    <Grid container direction="column" className={classes.container}>
      <Grid item>
        <form>
          <Controller
            control={control}
            name={getFieldName('selectedValue')}
            rules={{ required: true }}
            defaultValue={defaultValue}
            render={(fieldProps: any) => (
              <>
                <ReactSelect
                  name={getFieldName('selectedValue')}
                  value={fieldProps?.field?.value}
                  fields={permissions}
                  menuPosition="relative"
                  handleOnChange={fieldProps?.field?.onChange}
                  isMulti
                />
                <Validation touched={errors.selectedValue} error="Required" />
              </>
            )}
          />
        </form>
      </Grid>
      <Grid item>
        <ConfirmationPanel
          handleSubmit={handleSubmit(onSubmit)}
          handleCancel={() => {
            onCancel();
          }}
        />
      </Grid>
    </Grid>
  );
};

export const PermissionsFormStyled = withStyles(LicensedStatesTypeFormStyles)(PermissionsForm);
